import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  IconButton,
  LinearProgress
} from '@mui/material';
import {
  ExpandMore,
  ContentCopy,
  PostAdd,
  Business,
  TrendingUp,
  Insights,
  Web,
  AutoAwesome
} from '@mui/icons-material';
import { websiteAPI, EnhancedWebsiteInsights, WebsiteAnalysisResponse } from '../../services/websiteAPI';

interface EnhancedWebsiteAnalyzerProps {
  onGenerateContent?: (insights: EnhancedWebsiteInsights) => void;
  onAnalysisComplete?: (insights: EnhancedWebsiteInsights) => void;
}

export const EnhancedWebsiteAnalyzer: React.FC<EnhancedWebsiteAnalyzerProps> = ({
  onGenerateContent,
  onAnalysisComplete
}) => {
  const [url, setUrl] = useState('');
  const [maxPages, setMaxPages] = useState(5);
  const [analyzing, setAnalyzing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [insights, setInsights] = useState<EnhancedWebsiteInsights | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [generatingContent, setGeneratingContent] = useState(false);

  const analyzeWebsite = useCallback(async () => {
    if (!url.trim()) return;

    setAnalyzing(true);
    setError(null);
    setProgress(0);

    try {
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 10, 90));
      }, 500);

      const response: WebsiteAnalysisResponse = await websiteAPI.analyzeWebsite({
        url: url.trim(),
        max_pages: maxPages
      });

      clearInterval(progressInterval);
      setProgress(100);

      if (response.success) {
        setInsights(response.insights);
        onAnalysisComplete?.(response.insights);
      } else {
        throw new Error(response.error || 'Analysis failed');
      }

    } catch (err: any) {
      setError(err.message || 'Failed to analyze website. Please check the URL and try again.');
      console.error('Website analysis error:', err);
    } finally {
      setAnalyzing(false);
      setProgress(0);
    }
  }, [url, maxPages, onAnalysisComplete]);

  const generateContent = useCallback(async (contentType: 'post' | 'article' = 'post') => {
    if (!insights) return;

    setGeneratingContent(true);
    setError(null);

    try {
      const response = await websiteAPI.generateFromInsights({
        insights,
        content_type: contentType
      });

      if (response.success) {
        onGenerateContent?.(insights);
      } else {
        throw new Error(response.error || 'Content generation failed');
      }

    } catch (err: any) {
      setError(err.message || 'Failed to generate content from insights');
    } finally {
      setGeneratingContent(false);
    }
  }, [insights, onGenerateContent]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <Web sx={{ mr: 1 }} />
          Website Analysis
        </Typography>
        
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Website URL"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com"
              variant="outlined"
              disabled={analyzing}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              type="number"
              label="Max Pages"
              value={maxPages}
              onChange={(e) => setMaxPages(parseInt(e.target.value) || 5)}
              inputProps={{ min: 1, max: 10 }}
              disabled={analyzing}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Button
              variant="contained"
              onClick={analyzeWebsite}
              disabled={!url.trim() || analyzing}
              startIcon={analyzing ? <CircularProgress size={20} /> : <Insights />}
              fullWidth
              size="large"
            >
              {analyzing ? 'Analyzing...' : 'Analyze Website'}
            </Button>
          </Grid>
        </Grid>

        {analyzing && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Analyzing website content and extracting insights...
            </Typography>
          </Box>
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {insights && (
        <Box>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Button
                  variant="contained"
                  startIcon={generatingContent ? <CircularProgress size={20} /> : <PostAdd />}
                  onClick={() => generateContent('post')}
                  disabled={generatingContent}
                  fullWidth
                >
                  Generate LinkedIn Post
                </Button>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  variant="outlined"
                  startIcon={<AutoAwesome />}
                  onClick={() => generateContent('article')}
                  disabled={generatingContent}
                  fullWidth
                >
                  Generate Article
                </Button>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  variant="outlined"
                  startIcon={<ContentCopy />}
                  onClick={() => copyToClipboard(JSON.stringify(insights, null, 2))}
                  fullWidth
                >
                  Copy Insights
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Brand Identity */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Business sx={{ mr: 1 }} />
                <Typography variant="h6">Brand Identity</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        Brand Voice & Tone
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {insights.brand_identity.brand_voice_and_tone}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        Key Messaging Themes
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {insights.brand_identity.key_messaging_themes.map((theme, index) => (
                          <Chip key={index} label={theme} size="small" />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Content Opportunities */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUp sx={{ mr: 1 }} />
                <Typography variant="h6">Content Opportunities</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        LinkedIn Post Topics
                      </Typography>
                      <List dense>
                        {insights.content_opportunities.potential_linkedin_post_topics.map((topic, index) => (
                          <ListItem key={index}>
                            <ListItemText primary={topic} />
                            <IconButton 
                              size="small" 
                              onClick={() => copyToClipboard(topic)}
                            >
                              <ContentCopy fontSize="small" />
                            </IconButton>
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
    </Box>
  );
};

export default EnhancedWebsiteAnalyzer; 